import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';
import { Box, styled } from '@mui/material';

import { MuiCardForm } from 'lib/form/MuiCardForm';
import { KeyFileInputFormField } from 'lib/form/fields/KeyFileInputFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { SettingDisplay } from 'lib/SettingDisplay';
import { SettingDisplaySwitch } from '../SettingDisplaySwitch';
import { SettingDisplayText } from '../SettingDisplayText';

const PREFIX = 'MarketingSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2),
        overflowY: 'visible' // Needed for the dropdowns
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    }
}));

type IProps = {
    settings: IPublicAndPrivateSettings;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
};

export function NotificationsSettingsCard({ settings, onSubmit }: IProps) {
    return (
        <StyledMuiCardForm
            title="Notifications"
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
            initialValues={{
                appleTeamId: settings?.apns?.teamId,
                appleBundleIdentifier: settings?.app?.bundleIdentifier,
                appleKeyId: settings?.apns?.keyId,
                appleKeyFile: settings?.apns?.keyAsBase64,
                orderCreated: settings?.actionNotifications?.ORDER_CREATED?.email ?? false,
                orderUserBilled: settings?.actionNotifications?.ORDER_USER_BILLED?.email ?? false,
                orderCompleted: settings?.actionNotifications?.ORDER_COMPLETED?.email ?? false,
                userActivated: settings?.actionNotifications?.USER_ACTIVATED?.email ?? false
            }}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    <SettingDisplayText title="Apple Team ID" text={settings?.apns?.teamId} />
                    <SettingDisplayText
                        title="Apple Bundle Identifier"
                        buildtimeIOS
                        text={settings?.app?.bundleIdentifier}
                    />
                    <SettingDisplayText title="Apple Key ID" text={settings?.apns?.keyId} crop />
                    <SettingDisplayText title="Apple Key File" text={settings?.apns?.keyAsBase64} crop />
                    <SettingDisplaySwitch
                        title="Order Created"
                        checked={settings?.actionNotifications?.ORDER_CREATED?.email}
                    />
                    <SettingDisplaySwitch
                        title="Order User Billed"
                        checked={settings?.actionNotifications?.ORDER_USER_BILLED?.email}
                    />
                    <SettingDisplaySwitch
                        title="Order Completed"
                        checked={settings?.actionNotifications?.ORDER_COMPLETED?.email}
                    />
                    <SettingDisplaySwitch
                        title="User Activated"
                        checked={settings?.actionNotifications?.USER_ACTIVATED?.email}
                    />
                </Box>
            }
            childrenWhileEditing={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay label="Apple Team ID">
                        <TextFormField name="appleTeamId" />
                    </SettingDisplay>
                    <SettingDisplay label="Apple Bundle Identifier" buildtimeIOS>
                        <TextFormField name="appleBundleIdentifier" />
                    </SettingDisplay>
                    <SettingDisplay label="Apple Key ID">
                        <TextFormField name="appleKeyId" />
                    </SettingDisplay>
                    <SettingDisplay label="Apple Key File">
                        <KeyFileInputFormField name="appleKeyFile" />
                    </SettingDisplay>
                    <SettingDisplay label="Order Created">
                        <SwitchFormField name="orderCreated" />
                    </SettingDisplay>
                    <SettingDisplay label="Order User Billed">
                        <SwitchFormField name="orderUserBilled" />
                    </SettingDisplay>
                    <SettingDisplay label="Order Completed">
                        <SwitchFormField name="orderCompleted" />
                    </SettingDisplay>
                    <SettingDisplay label="User Activated">
                        <SwitchFormField name="userActivated" />
                    </SettingDisplay>
                </Box>
            }
        />
    );
}
